// src/services/annualreportService.js
// Nota: asegúrate de que el nombre del archivo y el import coincidan exactamente.
import { fetchWithAuth } from "../utils/fetchWithAuth";

const BASE = "/annual-reports";
const MIN_YEAR = 1976;
const MAX_YEAR = 2100;

/* ================== Helpers ================== */
const normalizeYear = (v) => {
  const n = Number(String(v ?? "").trim());
  return Number.isInteger(n) ? n : NaN;
};

const unwrapTextError = async (res, prefix) => {
  const text = await res.text().catch(() => "");
  throw new Error(`${prefix} ${res.status} ${res.statusText} ${text}`.trim());
};

const isValidCtaUrl = (s) => {
  if (!s) return true; // vacío se permite (lo valida el back igual)
  if (s.startsWith("mailto:")) return s.slice(7).includes("@");
  return /^https?:\/\//i.test(s);
};

/* ================== LIST (ADMIN) ================== */
export const listAnnualReports = async (year) => {
  const y = normalizeYear(year);
  const qs = Number.isInteger(y) ? `?year=${y}` : "";
  const res = await fetchWithAuth(`${BASE}${qs}`, { method: "GET" });
  if (!res.ok) return unwrapTextError(res, `GET ${BASE}`);
  const data = await res.json().catch(() => []);
  return Array.isArray(data) ? data : [];
};

/* ================== LIST (PUBLIC) ================== */
export const listAnnualReportsPublic = async (year) => {
  const y = normalizeYear(year);
  const qs = Number.isInteger(y) ? `?year=${y}` : "";
  const res = await fetchWithAuth(`${BASE}/public${qs}`, { method: "GET" });
  if (!res.ok) return unwrapTextError(res, `GET ${BASE}/public`);
  const data = await res.json().catch(() => []);
  return Array.isArray(data) ? data : [];
};

/* ================== UPLOAD ================== */
export const uploadAnnualReport = async ({ file, title, year, version /*, position*/ }) => {
  if (!file) throw new Error("Archivo requerido");
  const y = normalizeYear(year);
  if (!Number.isInteger(y) || y < MIN_YEAR || y > MAX_YEAR) {
    throw new Error(`Año inválido (${MIN_YEAR}–${MAX_YEAR})`);
  }

  const fd = new FormData();
  fd.append("file", file, file.name || "reporte.pdf");
  if (title != null) fd.append("title", String(title).trim());
  fd.append("year", String(y));
  if (version != null) fd.append("version", String(version).trim());
  // Si en el futuro editas posición desde el front:
  // if (Number.isInteger(position) && position > 0) fd.append("position", String(position));

  const res = await fetchWithAuth(`${BASE}/upload`, { method: "POST", body: fd });
  if (!res.ok) return unwrapTextError(res, `POST ${BASE}/upload`);
  return res.json();
};

/* ================== DOWNLOAD URL ================== */
export const downloadAnnualReportUrl = (id) =>
  id ? `${BASE}/${encodeURIComponent(id)}/download` : "#";

/* ================== UPDATE (título / año / posición / versión / visibilidad) ================== */
export const updateAnnualReport = async (
  id,
  { title, year, position, version, visibleForMembers } = {},
) => {
  if (!id) throw new Error("ID requerido");

  const body = {};
  if (title != null) body.title = String(title).trim();

  if (year != null) {
    const y = normalizeYear(year);
    if (!Number.isInteger(y) || y < MIN_YEAR || y > MAX_YEAR) {
      throw new Error(`Año inválido (${MIN_YEAR}–${MAX_YEAR})`);
    }
    body.year = y;
  }

  if (position != null) {
    const p = Number(position);
    if (!Number.isInteger(p) || p < 1) {
      throw new Error("position debe ser entero ≥ 1");
    }
    body.position = p;
  }

  if (version != null) body.version = String(version).trim();
  if (visibleForMembers != null) body.visibleForMembers = !!visibleForMembers;

  if (Object.keys(body).length === 0) {
    throw new Error("Nada que actualizar");
  }

  // 1) Intento con PUT
  let res = await fetchWithAuth(`${BASE}/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  // 2) Si el servidor NO soporta PUT, probamos PATCH
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    const saysCannotPUT = /cannot\s+put/i.test(txt);
    if (res.status === 405 || saysCannotPUT || (res.status === 404 && /cannot\s+put|not\s+found/i.test(txt))) {
      res = await fetchWithAuth(`${BASE}/${encodeURIComponent(id)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    } else {
      throw new Error(`UPDATE ${BASE}/${id} ${res.status} ${res.statusText} ${txt}`.trim());
    }
  }

  if (!res.ok) return unwrapTextError(res, `UPDATE ${BASE}/${id}`);
  return res.json();
};

/* ================== DELETE ================== */
export const deleteAnnualReport = async (id) => {
  const res = await fetchWithAuth(`${BASE}/${encodeURIComponent(id)}`, { method: "DELETE" });
  if (!res.ok) return unwrapTextError(res, `DELETE ${BASE}/${id}`);
  return res.json();
};

/* ================== HEADLINE POR AÑO ================== */
export const getAnnualReportYearTitle = async (year) => {
  const y = normalizeYear(year);
  if (!Number.isInteger(y)) throw new Error("Año requerido");
  const res = await fetchWithAuth(`${BASE}/year-title?year=${y}`, { method: "GET" });
  if (!res.ok) return unwrapTextError(res, `GET ${BASE}/year-title`);
  return res.json(); // { year, headline|null, updatedAt? }
};

export const saveAnnualReportYearTitle = async (year, headline) => {
  const y = normalizeYear(year);
  if (!Number.isInteger(y)) throw new Error("Año requerido");
  const payload = { year: y, headline: headline ?? "" };
  const res = await fetchWithAuth(`${BASE}/year-title`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) return unwrapTextError(res, `PUT ${BASE}/year-title`);
  return res.json(); // { year, headline|null, updatedAt? }
};

/* ================== CONFIG DE PÁGINA (títulos + CTA + principios) ================== */
export const getAnnualReportsConfig = async () => {
  const res = await fetchWithAuth(`${BASE}/config`, { method: "GET" });
  if (!res.ok) return unwrapTextError(res, `GET ${BASE}/config`);
  // Esperado: { pageTitle?, subtitle?, previousTitle?, ctaEnabled?, ctaLabel?, ctaUrl?, principlesVisible?, lastVisibleAt?, updatedAt? }
  return res.json();
};

export const updateAnnualReportsConfig = async ({
  pageTitle,
  subtitle,
  previousTitle,
  ctaEnabled,
  ctaLabel,
  ctaUrl,
  principlesVisible,
} = {}) => {
  if (ctaEnabled) {
    if (!ctaLabel || !String(ctaLabel).trim()) {
      throw new Error("CTA: debes indicar el nombre del botón.");
    }
    if (!isValidCtaUrl(String(ctaUrl || "").trim())) {
      throw new Error("CTA: la URL debe iniciar con http(s):// o mailto:");
    }
  }

  const payload = {};
  if (pageTitle != null) payload.pageTitle = String(pageTitle).trim();
  if (subtitle != null) payload.subtitle = String(subtitle).trim();
  if (previousTitle != null) payload.previousTitle = String(previousTitle).trim();
  if (principlesVisible != null) payload.principlesVisible = !!principlesVisible;
  if (ctaEnabled != null) payload.ctaEnabled = !!ctaEnabled;
  if (ctaLabel != null) payload.ctaLabel = String(ctaLabel).trim();
  if (ctaUrl != null) payload.ctaUrl = String(ctaUrl).trim();

  const res = await fetchWithAuth(`${BASE}/config`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) return unwrapTextError(res, `PUT ${BASE}/config`);
  return res.json();
};

/* ================== REORDENAR (bulk) POR AÑO ================== */
/**
 * Persiste el orden de un año: `ids` en el orden final deseado.
 * Backend asigna position = índice + 1 (1..n).
 * Devuelve la lista del año ya ordenada.
 */
export const reorderAnnualReports = async (year, ids) => {
  const y = normalizeYear(year);
  if (!Number.isInteger(y) || y < MIN_YEAR || y > MAX_YEAR) {
    throw new Error(`Año inválido (${MIN_YEAR}–${MAX_YEAR})`);
  }
  if (!Array.isArray(ids) || ids.length === 0) {
    throw new Error("ids requerido (array no vacío)");
  }
  const res = await fetchWithAuth(`${BASE}/reorder/${y}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids }),
  });
  if (!res.ok) return unwrapTextError(res, `PATCH ${BASE}/reorder/${y}`);
  const data = await res.json().catch(() => []);
  return Array.isArray(data) ? data : [];
};
